import { useEffect, useState } from "react";
import { Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { webApi } from "@/lib/webApi";
import { MediaPicker, HeroTextFields } from "@/components/admin/MediaPicker";

const fmt = (iso) => new Date(iso).toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
const status = (s) => {
  const now = Date.now();
  if (new Date(s.ends_at) <= now) return ["Ended", "bg-[#F5EFE6] text-[#71717A]"];
  if (new Date(s.starts_at) <= now) return ["Live now", "bg-[#C5A059] text-[#0F0F0F]"];
  return ["Upcoming", "bg-[#0F0F0F] text-[#C5A059]"];
};
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export const ScheduleManager = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState(null);
  const [text, setText] = useState({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => webApi.get("/hero/schedules").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!media || !start || !end) return toast.error("Choose a banner and both dates.");
    if (new Date(end) <= new Date(start)) return toast.error("End must be after start.");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", media.file);
      form.append("starts_at", new Date(start).toISOString());
      form.append("ends_at", new Date(end).toISOString());
      Object.entries(text).forEach(([k, v]) => v?.trim() && form.append(k, v.trim()));
      await webApi.post("/hero/schedules", form);
      toast.success("Banner scheduled");
      URL.revokeObjectURL(media.url);
      setMedia(null); setText({}); setStart(""); setEnd("");
      load(); onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await webApi.delete(`/hero/schedules/${id}`);
      setItems((l) => l.filter((s) => s.id !== id));
      toast("Scheduled banner removed");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="schedule-manager" className="space-y-6">
      <ul data-testid="schedule-list" className="space-y-2">
        {items.length === 0 && <li className="text-sm text-[#71717A]">No banners scheduled yet.</li>}
        {items.map((s) => {
          const [label, cls] = status(s);
          return (
            <li key={s.id} data-testid={`schedule-item-${s.id}`} className="flex items-center gap-3 bg-white border border-[#0F0F0F]/10 p-2">
              <div className="h-12 w-20 bg-[#0F0F0F] shrink-0 overflow-hidden">
                {s.media_type === "video" ? <video src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} muted className="h-full w-full object-cover" /> : <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{s.headline || "Default headline"}</p>
                <p className="font-mono text-[10px] text-[#71717A]">{fmt(s.starts_at)} → {fmt(s.ends_at)}</p>
              </div>
              <span data-testid={`schedule-status-${s.id}`} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 shrink-0 ${cls}`}>{label}</span>
              <button data-testid={`schedule-delete-${s.id}`} aria-label="Remove" onClick={() => remove(s.id)} className="p-2 text-[#71717A] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </li>
          );
        })}
      </ul>

      <form onSubmit={add} className="space-y-4 border-t border-[#C5A059]/25 pt-5">
        <p className="eyebrow flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Schedule a new banner</p>
        <MediaPicker value={media} onChange={setMedia} testid="schedule" />
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Starts</span>
            <input data-testid="schedule-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="w-full bg-white border border-[#0F0F0F]/15 px-2 py-2 text-sm" />
          </label>
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Ends</span>
            <input data-testid="schedule-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full bg-white border border-[#0F0F0F]/15 px-2 py-2 text-sm" />
          </label>
        </div>
        <HeroTextFields value={text} onChange={setText} testid="schedule" />
        <button data-testid="schedule-save-button" type="submit" disabled={busy} className="w-full bg-[#0F0F0F] text-[#C5A059] py-3 text-xs tracking-[0.3em] uppercase disabled:opacity-40">
          {busy ? "Scheduling…" : "Schedule banner"}
        </button>
      </form>
    </div>
  );
};
